import React, { useEffect, useState, useMemo } from "react";
import DataTable from "react-data-table-component";
import {
    AiOutlineDownload,
    AiOutlineSearch,
    AiOutlineCloudUpload,
    AiOutlineDown,
    AiOutlineFileText,
    AiOutlineCheckCircle,
    AiOutlineClockCircle,
    AiOutlineUser,
    AiOutlineCalendar,
    AiOutlineFolderOpen,
    AiOutlineLeft,
    AiOutlineRight
} from "react-icons/ai";
import { Button, Dropdown } from "react-bootstrap";
import authService from "../../services/authService";

// ----- REAL API -----
import { API_CONFIG } from '../../config/env';
const API_BASE_URL = `${API_CONFIG.BASE_URL}/admin/documents`;

async function apiGetUserUploads(statusFilter = '', yearFilter = '') {
    let url = `${API_BASE_URL}/uploads/?t=${Date.now()}&_cb=${Math.random()}`;
    if (statusFilter && statusFilter !== 'All Statuses') {
        url += `&status=${statusFilter}`;
    }
    if (yearFilter && yearFilter !== 'All Years') {
        url += `&year=${yearFilter}`;
    }
    
    const token = localStorage.getItem('adminAuthToken');
    const response = await fetch(url, {
        headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
        }
    });
    
    if (!response.ok) {
        console.error('API Error:', response.status, response.statusText);
        const errorText = await response.text();
        console.error('Error response:', errorText);
        
        // Handle authentication errors
        if (response.status === 401) {
            console.log('Authentication failed - redirecting to login');
            localStorage.removeItem('adminAuthToken');
            window.location.href = '/admin/login';
            return [];
        }
        
        throw new Error(`Failed to fetch user uploads: ${response.status} ${response.statusText}`);
    }
    
    const data = await response.json();
    console.log('API Response:', data);
    console.log('API Response data field:', data.data);
    console.log('API Response data type:', typeof data.data);
    console.log('API Response data length:', data.data ? data.data.length : 'null/undefined');
    return data.data || [];
}

async function apiDownloadFile({ id, filename }) {
    const token = localStorage.getItem('adminAuthToken');
    const response = await fetch(`${API_BASE_URL}/uploads/${id}/download/`, {
        headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
        }
    });
    
    if (!response.ok) {
        throw new Error('Failed to get download URL');
    }
    
    const data = await response.json();
    if (data.success && data.download_url) {
        // Open download URL in new tab
        window.open(data.download_url, '_blank');
    } else {
        alert('Download failed: ' + (data.error || 'Unknown error'));
    }
}

async function apiDownloadUserFilesZip(userId, year) {
    const response = await authService.adminApiCall(`${API_BASE_URL}/download-zip/?user_id=${userId}&year=${year}`);
    
    if (!response.ok) {
        throw new Error('Failed to download zip file');
    }
    
    // Check if response is a zip file
    const contentType = response.headers.get('content-type');
    if (contentType && contentType.includes('application/zip')) {
        // Get the filename from Content-Disposition header
        const contentDisposition = response.headers.get('content-disposition');
        let filename = 'user_files.zip';
        if (contentDisposition) {
            const filenameMatch = contentDisposition.match(/filename="(.+)"/);
            if (filenameMatch) {
                filename = filenameMatch[1];
            }
        }
        
        // Create blob and download
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
    } else {
        // Handle error response
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to download zip file');
    }
}

async function apiDownloadDraftCopy(taxReturnId) {
    const token = localStorage.getItem('adminAuthToken');
    const response = await fetch(`${API_BASE_URL}/tax-returns/${taxReturnId}/download/`, {
        headers: {
            'Authorization': `Bearer ${token}`,
        }
    });
    
    if (!response.ok) {
        throw new Error('Failed to download draft copy');
    }
    
    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `draft_copy_${taxReturnId}.pdf`;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
}

async function apiDownloadFinalCopy(taxReturnId) {
    const token = localStorage.getItem('adminAuthToken');
    const response = await fetch(`${API_BASE_URL}/tax-returns/${taxReturnId}/download/`, {
        headers: {
            'Authorization': `Bearer ${token}`,
        }
    });
    
    if (!response.ok) {
        throw new Error('Failed to download final copy');
    }
    
    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `final_copy_${taxReturnId}.pdf`;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
}

// New API functions for tax return workflow
async function apiUploadDraftCopy(userId, year, file) {
    const token = localStorage.getItem('adminAuthToken');
    const formData = new FormData();
    formData.append('user_id', userId);
    formData.append('year', year);
    formData.append('file', file);
    formData.append('type', 'draft');

    const response = await fetch(`${API_BASE_URL}/tax-returns/upload/`, {
        method: 'POST',
        headers: {
            'Authorization': `Bearer ${token}`
        },
        body: formData
    });

    if (!response.ok) {
        throw new Error('Failed to upload draft copy');
    }

    return await response.json();
}

async function apiUploadFinalCopy(userId, year, file) {
    const token = localStorage.getItem('adminAuthToken');
    // Token validation completed
    
    if (!token) {
        throw new Error('Authentication token not found. Please log in again.');
    }
    
    const formData = new FormData();
    formData.append('user_id', userId);
    formData.append('year', year);
    formData.append('file', file);
    formData.append('type', 'final');

    // Uploading final copy

    const response = await fetch(`${API_BASE_URL}/tax-returns/upload/`, {
        method: 'POST',
        headers: {
            'Authorization': `Bearer ${token}`
        },
        body: formData
    });

    // Upload response received

    if (!response.ok) {
        const errorText = await response.text();
        console.error('Upload error response:', errorText);
        throw new Error(`Failed to upload final copy: ${response.status} ${response.statusText}`);
    }

    return await response.json();
}

async function apiGetTaxReturnStatus(userId, year) {
    const token = localStorage.getItem('adminAuthToken');
    const response = await fetch(`${API_BASE_URL}/tax-returns/status/?user_id=${userId}&year=${year}`, {
        headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
        }
    });

    if (!response.ok) {
        throw new Error('Failed to get tax return status');
    }

    return await response.json();
}

async function apiGetAllTaxReturnStatuses() {
    const token = localStorage.getItem('adminAuthToken');
    const response = await fetch(`${API_BASE_URL}/tax-returns/?t=${Date.now()}&_cb=${Math.random()}`, {
        headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
        }
    });
    
    if (!response.ok) {
        console.error('Tax Returns API Error:', response.status, response.statusText);
        const errorText = await response.text();
        console.error('Tax Returns Error response:', errorText);
        
        // Handle authentication errors
        if (response.status === 401) {
            console.log('Authentication failed - redirecting to login');
            localStorage.removeItem('adminAuthToken');
            window.location.href = '/admin/login';
            return [];
        }
        
        throw new Error(`Failed to get tax return statuses: ${response.status} ${response.statusText}`);
    }
    
    const data = await response.json();
    return data.data || [];
}


// --------------------------

export default function UserUploads() {
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [perPage, setPerPage] = useState(10);
    const [currentPage, setCurrentPage] = useState(1);
    const [search, setSearch] = useState("");
    const [selectedYear, setSelectedYear] = useState("All Years");
    const [selectedStatus, setSelectedStatus] = useState("All Statuses");

    // New state for workflow management
    const [selectedUser, setSelectedUser] = useState(null);
    const [showDraftModal, setShowDraftModal] = useState(false);
    const [showFinalModal, setShowFinalModal] = useState(false);
    const [draftFile, setDraftFile] = useState(null);
    const [finalFile, setFinalFile] = useState(null);
    const [uploadLoading, setUploadLoading] = useState(false);
    const [taxReturnStatus, setTaxReturnStatus] = useState({});
    const [successMessage, setSuccessMessage] = useState("");

    // New state for user selection and view modes
    const [viewMode, setViewMode] = useState('overview'); // 'overview' or 'user-detail'
    const [selectedUserForDetail, setSelectedUserForDetail] = useState(null);
    const [selectedYearForUser, setSelectedYearForUser] = useState("All Years");
    const [selectedUserName, setSelectedUserName] = useState("");
    const [currentYearIndex, setCurrentYearIndex] = useState(0);

    useEffect(() => {
        async function fetchData() {
            setLoading(true);
            try {
                // Check if token exists and is valid
                const token = localStorage.getItem('adminAuthToken');
                if (!token) {
                    console.log('No authentication token found - redirecting to login');
                    window.location.href = '/admin/login';
                    return;
                }
                
                // Clear any cached data first
                setData([]);
                setTaxReturnStatus({});
                
                // Clear all possible caches
                localStorage.removeItem('adminDataCache');
                sessionStorage.clear();
                
                // Clear any browser cache
                if ('caches' in window) {
                    const cacheNames = await caches.keys();
                    await Promise.all(
                        cacheNames.map(cacheName => caches.delete(cacheName))
                    );
                }
                
                // Force a small delay to ensure cache clearing
                await new Promise(resolve => setTimeout(resolve, 100));
                
                // Fetch user uploads and tax return statuses in parallel
                const [uploadsData, taxReturnsData] = await Promise.all([
                    apiGetUserUploads(selectedStatus, selectedYear),
                    apiGetAllTaxReturnStatuses()
                ]);
                
                // Process API responses
                
                // Check if uploads data is valid
                if (!uploadsData || !Array.isArray(uploadsData)) {
                    console.error('Invalid initial uploads data:', uploadsData);
                    console.log('Setting empty data due to invalid uploads data...');
                    setData([]);
                    setTaxReturnStatus({});
                    setLoading(false);
                    return;
                }
                
                // Handle empty data case
                if (uploadsData.length === 0) {
                    console.log('No uploads data found - setting empty data');
                    setData([]);
                    setTaxReturnStatus({});
                    setLoading(false);
                    return;
                }
                
                // Filter out any invalid uploads
                const validUploads = uploadsData.filter(upload => 
                    upload && upload.user && upload.year_display
                );
                
                if (validUploads.length === 0) {
                    console.log('No valid uploads found after filtering');
                    setData([]);
                    setTaxReturnStatus({});
                    setLoading(false);
                    return;
                }
                
                // Process uploads data to include draft and final copy information
                // Ensure we have valid data to process
                if (!uploadsData || uploadsData.length === 0) {
                    setData([]);
                    setTaxReturnStatus({});
                    setLoading(false);
                    return;
                }
                
                const processedData = validUploads.map(upload => {
                        // Find draft and final copies for this user and year
                        const userTaxReturns = taxReturnsData.filter(tr => 
                            tr.user === upload.user && tr.year === upload.year_display
                        );
                        
                        const draftCopy = userTaxReturns.find(tr => tr.type === 'draft');
                        const finalCopy = userTaxReturns.find(tr => tr.type === 'final');
                        
                        // Start with status from API response (which already has correct logic from backend)
                        let status = upload.status || 'pending';
                        let draftCopyInfo = draftCopy ? draftCopy.filename : null;
                        let draftCopyId = draftCopy ? draftCopy.id : null;
                        let finalCopyInfo = finalCopy ? finalCopy.filename : null;
                        let finalCopyId = finalCopy ? finalCopy.id : null;
                    
                        // Only override status if we have tax return data that indicates a more advanced state
                        // The backend API already sets status correctly, so we trust it unless tax returns show otherwise
                        if (finalCopy && finalCopy.user_received_at) {
                            // User has acknowledged receipt - this is the most advanced state
                            status = 'completed';
                        } else if (finalCopy && finalCopy.status === 'final_uploaded') {
                            // Final copy uploaded but not yet received by user
                            status = 'final_uploaded';
                        } else if (draftCopy && draftCopy.is_submitted) {
                            // Draft has been approved by user
                            status = 'draft_approved';
                        } else if (draftCopy && draftCopy.status === 'draft_uploaded') {
                            // Draft uploaded but not yet approved
                            status = 'draft_uploaded';
                        } else if (upload.status) {
                            // Use the status from API response (backend has already determined it correctly)
                            status = upload.status;
                        } else {
                            // Fallback to pending only if no status is available
                            status = 'pending';
                        }
                    
                    return {
                        ...upload,
                        user_name: upload.user_name || 'Error loading user',
                        category_name: upload.category_name || 'Error loading category',
                        year_display: upload.year_display || 'Error loading year',
                        draft_copy: draftCopyInfo || upload.draft_copy,  // Use from upload if tax return doesn't have it
                        draft_copy_id: draftCopyId || upload.draft_copy_id,
                        final_copy: finalCopyInfo || upload.final_copy,
                        final_copy_id: finalCopyId || upload.final_copy_id,
                        status: status
                    };
                });
                
                setData(processedData);
                
                // Process tax return statuses into a lookup object
                const statusLookup = {};
                if (taxReturnsData && taxReturnsData.length > 0) {
                    taxReturnsData.forEach(taxReturn => {
                        const key = `${taxReturn.user}_${taxReturn.year}`;
                        // Check if final copy has been received by user
                        if (taxReturn.type === 'final' && taxReturn.user_received_at) {
                            statusLookup[key] = 'user_received';
                        } else if (taxReturn.type === 'final' && taxReturn.status === 'final_uploaded') {
                            statusLookup[key] = 'completed'; // Show as "Final Copy Sent"
                        } else {
                            statusLookup[key] = taxReturn.status;
                        }
                    });
                }
                setTaxReturnStatus(statusLookup);
                
            } catch (error) {
                console.error('Error fetching data:', error);
                setData([]);
                setTaxReturnStatus({});
                alert('Failed to load data. Please check console for details.');
            } finally {
                setLoading(false);
            }
        }
        fetchData();
    }, []);

    // Auto-refresh for status updates (only when page is visible)
    useEffect(() => {
        const handleVisibilityChange = () => {
            if (!document.hidden) {
                // Page became visible, refresh data
                refreshData();
            }
        };

        // Refresh when page becomes visible
        document.addEventListener('visibilitychange', handleVisibilityChange);

        // Auto-refresh every 30 seconds when page is visible
        const interval = setInterval(() => {
            if (!document.hidden) {
                refreshData();
            }
        }, 30000);

        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
            clearInterval(interval);
        };
    }, []);

    // Component rendering with data
    
    const columns = useMemo(() => [
        {
            name: "S.No",
            width: "60px",
            cell: (_row, idx) => (currentPage - 1) * perPage + idx + 1,
            style: { fontWeight: 600, color: "#4338ca", textAlign: "center", whiteSpace: "nowrap" },
            sortable: false,
            center: true,
        },
        {
            name: "User Name",
            selector: row => row.user_name,
            sortable: true,
            minWidth: "130px",
            style: { fontWeight: 500, whiteSpace: "nowrap" },
            cell: row => <span>{row.user_name || 'N/A'}</span>
        },
        {
            name: "File Name",
            selector: row => row.original_filename,
            sortable: true,
            minWidth: "200px",
            style: { 
                fontSize: "12px",
                overflow: "hidden",
                textOverflow: "ellipsis"
            },
            cell: row => (
                <span 
                    style={{ 
                        display: "block",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        maxWidth: "100%"
                    }}
                    title={row.original_filename || 'N/A'}
                >
                    {row.original_filename || 'N/A'}
                </span>
            )
        },
        {
            name: "Category",
            selector: row => row.category_name,
            sortable: true,
            minWidth: "120px",
            style: { color: "#217eed", fontWeight: 500, whiteSpace: "nowrap" },
            cell: row => <span>{row.category_name || 'N/A'}</span>
        },
        {
            name: "Tax Year",
            selector: row => row.year_display,
            sortable: true,
            width: "90px",
            style: { 
                fontWeight: 600, 
                color: "#112", 
                textAlign: "center",
                fontSize: "12px"
            },
            cell: row => <span>{row.year_display || 'N/A'}</span>
        },
        {
            name: "Status",
            selector: row => row.status,
            sortable: true,
            minWidth: "100px",
            style: { textAlign: "center", whiteSpace: "nowrap" },
            cell: row => (
                <span style={{
                    borderRadius: "12px",
                    padding: "4px 12px",
                    fontWeight: 600,
                    color: "#fff",
                    fontSize: 12,
                    background: row.status === "completed" ? "#21bb55" :
                               row.status === "final_uploaded" ? "#f59e0b" :
                               row.status === "draft_approved" ? "#3b82f6" :
                               row.status === "draft_uploaded" ? "#6366f1" :
                               row.status === "pending" ? "#f59e0b" :
                               row.status === "approved" ? "#21bb55" : 
                               row.status === "rejected" ? "#dc2626" : 
                               row.status === "processing" ? "#f59e0b" : "#6b7280",
                    display: "inline-block",
                    minWidth: 60
                }}>
                    {row.status === "completed" ? "Completed" :
                     row.status === "final_uploaded" ? "Final Copy Sent" :
                     row.status === "draft_approved" ? "Draft Approved" :
                     row.status === "draft_uploaded" ? "Draft Uploaded" :
                     row.status === "pending" ? "Pending" :
                     (row.status || 'N/A')}
                </span>
            ),
            center: true
        },
        {
            name: "Draft Copy",
            selector: row => row.draft_copy,
            sortable: false,
            width: "120px",
            style: { textAlign: "center" },
            cell: row => {
                if (row.draft_copy) {
                    return (
                        <button
                            onClick={() => handleDownloadDraft(row)}
                            style={{
                                background: "#3b82f6",
                                color: "#fff",
                                border: "none",
                                borderRadius: "4px",
                                padding: "4px 8px",
                                fontSize: "11px",
                                cursor: "pointer",
                                display: "flex",
                                alignItems: "center",
                                gap: "4px"
                            }}
                            title="Download Draft Copy"
                        >
                            <AiOutlineDownload style={{ fontSize: "12px" }} />
                            Download
                        </button>
                    );
                }
                return <span style={{ color: "#9ca3af", fontSize: "12px" }}>N/A</span>;
            }
        },
        {
            name: "Final Copy",
            selector: row => row.final_copy,
            sortable: false,
            width: "120px",
            style: { textAlign: "center" },
            cell: row => {
                if (row.final_copy) {
                    return (
                        <button
                            onClick={() => handleDownloadFinal(row)}
                            style={{
                                background: "#10b981",
                                color: "#fff",
                                border: "none",
                                borderRadius: "4px",
                                padding: "4px 8px",
                                fontSize: "11px",
                                cursor: "pointer",
                                display: "flex",
                                alignItems: "center",
                                gap: "4px"
                            }}
                            title="Download Final Copy"
                        >
                            <AiOutlineDownload style={{ fontSize: "12px" }} />
                            Download
                        </button>
                    );
                }
                return <span style={{ color: "#9ca3af", fontSize: "12px" }}>N/A</span>;
            }
        },
        {
            name: "Upload Date (CST)",
            selector: row => row.uploaded_at,
            sortable: true,
            minWidth: "160px",
            style: { 
                color: "#555", 
                fontSize: "11px",
                overflow: "hidden",
                textOverflow: "ellipsis"
            },
            cell: row => (
                <span 
                    style={{
                        display: "block",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        maxWidth: "100%"
                    }}
                    title={row.uploaded_at ? formatToCST(row.uploaded_at) : 'N/A'}
                >
                    {row.uploaded_at ? formatToCST(row.uploaded_at) : 'N/A'}
                </span>
            )
        },
    ], [currentPage, perPage]);

    async function handleDownload(row) {
        setLoading(true);
        await apiDownloadFile({ id: row.id, filename: row.filename });
        setLoading(false);
    }

    function handlePageChange(page) { setCurrentPage(page); }
    function handlePerRowsChange(newPerPage, page) { setPerPage(newPerPage); setCurrentPage(page); }
    
    // Reset filters
    function resetFilters() {
        setSearch("");
        setSelectedYear("All Years");
        setSelectedStatus("All Statuses");
        setCurrentPage(1);
    }


    // View switching functions
    function switchToUserDetail(user) {
        setSelectedUserForDetail(user);
        setSelectedUserName(user.user_name);
        setSelectedYearForUser("All Years");
        setCurrentYearIndex(0);
        setViewMode('user-detail');
    }

    function switchToOverview() {
        setSelectedUserForDetail(null);
        setSelectedUserName("");
        setSelectedYearForUser("All Years");
        setCurrentYearIndex(0);
        setViewMode('overview');
    }


    function handleYearSelection(e) {
        setSelectedYearForUser(e.target.value);
        setCurrentYearIndex(0);
    }

    function handleUserNameSelection(e) {
        setSelectedUserName(e.target.value);
        setCurrentYearIndex(0);
    }

    // Year navigation functions
    function goToPreviousYear() {
        if (currentYearIndex > 0) {
            setCurrentYearIndex(currentYearIndex - 1);
        }
    }

    function goToNextYear() {
        if (currentYearIndex < selectedUserGroupedData.length - 1) {
            setCurrentYearIndex(currentYearIndex + 1);
        }
    }

    // Format date to CST timezone
    function formatToCST(dateString) {
        const date = new Date(dateString);
        return date.toLocaleString('en-US', {
            timeZone: 'America/Chicago',
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            hour12: false
        });
    }

    // New workflow functions
    function openDraftModal(userData) {
        setSelectedUser(userData);
        setShowDraftModal(true);
        setDraftFile(null);
    }

    function openFinalModal(userData) {
        setSelectedUser(userData);
        setShowFinalModal(true);
        setFinalFile(null);
    }

    function closeDraftModal() {
        setShowDraftModal(false);
        setSelectedUser(null);
        setDraftFile(null);
    }

    function closeFinalModal() {
        setShowFinalModal(false);
        setSelectedUser(null);
        setFinalFile(null);
    }

    function handleDraftFileChange(e) {
        const file = e.target.files[0];
        if (file) {
            setDraftFile(file);
        }
    }

    function handleFinalFileChange(e) {
        const file = e.target.files[0];
        if (file) {
            setFinalFile(file);
        }
    }

    async function handleDraftUpload() {
        if (!draftFile || !selectedUser) return;

        setUploadLoading(true);
        try {
            await apiUploadDraftCopy(selectedUser.user_id, selectedUser.year_display, draftFile);
            
            // Show success message
            setSuccessMessage(`Draft copy uploaded successfully for ${selectedUser.user_name} (${selectedUser.year_display})`);
            
            // Refresh data and tax return statuses
            const [uploads, taxReturnsData] = await Promise.all([
                apiGetUserUploads(selectedStatus, selectedYear),
                apiGetAllTaxReturnStatuses()
            ]);
            
            console.log('Uploads data:', uploads);
            console.log('Tax returns data:', taxReturnsData);
            
            // Check if uploads data is valid
            if (!uploads || !Array.isArray(uploads)) {
                console.error('Invalid uploads data:', uploads);
                alert('Failed to refresh data. Please reload the page.');
                return;
            }
            
            // Process uploads data to include draft and final copy information
            const processedData = uploads.map(upload => {
                // Find draft and final copies for this user and year
                const userTaxReturns = taxReturnsData.filter(tr => 
                    tr.user === upload.user && tr.year === upload.year_display
                );
                
                const draftCopy = userTaxReturns.find(tr => tr.type === 'draft');
                const finalCopy = userTaxReturns.find(tr => tr.type === 'final');
                
                
                // Start with status from API response (backend has already determined it correctly)
                let status = upload.status || 'pending';
                
                // Only override status if we have tax return data that indicates a more advanced state
                // The backend API already sets status correctly, so we trust it unless tax returns show otherwise
                if (finalCopy && finalCopy.user_received_at) {
                    // User has acknowledged receipt - this is the most advanced state
                    status = 'completed';
                } else if (finalCopy && finalCopy.status === 'final_uploaded') {
                    // Final copy uploaded but not yet received by user
                    status = 'final_uploaded';
                } else if (draftCopy && draftCopy.is_submitted) {
                    // Draft has been approved by user
                    status = 'draft_approved';
                } else if (draftCopy && draftCopy.status === 'draft_uploaded') {
                    // Draft uploaded but not yet approved
                    status = 'draft_uploaded';
                } else if (upload.status) {
                    // Use the status from API response (backend has already determined it correctly)
                    status = upload.status;
                } else {
                    // Fallback to pending only if no status is available
                    status = 'pending';
                }
                
                return {
                    ...upload,
                    draft_copy: draftCopy ? draftCopy.filename : null,
                    draft_copy_id: draftCopy ? draftCopy.id : null,
                    final_copy: finalCopy ? finalCopy.filename : null,
                    final_copy_id: finalCopy ? finalCopy.id : null,
                    status: status
                };
            });
            
            // Only update data if we have valid processed data
            if (processedData && processedData.length > 0) {
                setData(processedData);
            } else {
                console.warn('No valid processed data, keeping existing data');
            }
            
            // Update tax return status lookup
            const statusLookup = {};
            taxReturnsData.forEach(taxReturn => {
                const key = `${taxReturn.user}_${taxReturn.year}`;
                statusLookup[key] = taxReturn.status;
            });
            setTaxReturnStatus(statusLookup);
            
            closeDraftModal();
            
            // Success message will be cleared manually by user or on next action
            
        } catch (error) {
            console.error('Draft upload failed:', error);
            alert('Failed to upload draft. Please try again.');
        } finally {
            setUploadLoading(false);
        }
    }

    async function handleFinalUpload() {
        if (!finalFile || !selectedUser) return;

        console.log('Starting final upload for:', selectedUser);
        setUploadLoading(true);
        try {
            // Check if token exists before upload
            const token = localStorage.getItem('adminAuthToken');
            if (!token) {
                alert('Authentication token not found. Please log in again.');
                return;
            }
            
            const response = await apiUploadFinalCopy(selectedUser.user_id, selectedUser.year_display, finalFile);
            console.log('Upload response:', response);
            
            // Show success message
            console.log('Setting success message');
            setSuccessMessage(`Final copy uploaded successfully for ${selectedUser.user_name} (${selectedUser.year_display})`);
            
            // Refresh data and tax return statuses
            console.log('Refreshing data...');
            const [uploads, taxReturnsData] = await Promise.all([
                apiGetUserUploads(selectedStatus, selectedYear),
                apiGetAllTaxReturnStatuses()
            ]);
            
            console.log('Uploads data:', uploads);
            console.log('Tax returns data:', taxReturnsData);
            
            // Check if uploads data is valid
            if (!uploads || !Array.isArray(uploads)) {
                console.error('Invalid uploads data:', uploads);
                alert('Failed to refresh data. Please reload the page.');
                return;
            }
            
            // Process uploads data to include draft and final copy information
            const processedData = uploads.map(upload => {
                // Find draft and final copies for this user and year
                const userTaxReturns = taxReturnsData.filter(tr => 
                    tr.user === upload.user && tr.year === upload.year_display
                );
                
                const draftCopy = userTaxReturns.find(tr => tr.type === 'draft');
                const finalCopy = userTaxReturns.find(tr => tr.type === 'final');
                
                // Update status based on workflow progression
                const updatedUpload = {
                    ...upload,
                    draft_copy: draftCopy ? draftCopy.filename : null,
                    draft_copy_id: draftCopy ? draftCopy.id : null,
                    final_copy: finalCopy ? finalCopy.filename : null,
                    final_copy_id: finalCopy ? finalCopy.id : null
                };
                
                // Start with status from API response (backend has already determined it correctly)
                let status = upload.status || 'pending';
                
                // Only override status if we have tax return data that indicates a more advanced state
                if (finalCopy && finalCopy.user_received_at) {
                    status = 'completed';
                } else if (finalCopy && finalCopy.status === 'final_uploaded') {
                    status = 'final_uploaded';
                } else if (draftCopy && draftCopy.is_submitted) {
                    status = 'draft_approved';
                } else if (draftCopy && draftCopy.status === 'draft_uploaded') {
                    status = 'draft_uploaded';
                } else if (upload.status) {
                    // Use the status from API response (backend has already determined it correctly)
                    status = upload.status;
                } else {
                    status = 'pending';
                }
                
                updatedUpload.status = status;
                
                return updatedUpload;
            });
            
            // Only update data if we have valid processed data
            if (processedData && processedData.length > 0) {
                setData(processedData);
            } else {
                console.warn('No valid processed data, keeping existing data');
            }
            
            // Update tax return status lookup
            const statusLookup = {};
            taxReturnsData.forEach(taxReturn => {
                const key = `${taxReturn.user}_${taxReturn.year}`;
                // Check if final copy has been received by user
                if (taxReturn.type === 'final' && taxReturn.user_received_at) {
                    statusLookup[key] = 'user_received';
                } else if (taxReturn.type === 'final' && taxReturn.status === 'final_uploaded') {
                    statusLookup[key] = 'completed'; // Show as "Final Copy Sent"
                } else {
                    statusLookup[key] = taxReturn.status;
                }
                // Status logging removed for better performance
            });
            setTaxReturnStatus(statusLookup);
            
            // Status updated successfully
            
            closeFinalModal();
            
            // Success message will be cleared manually by user or on next action
            
        } catch (error) {
            console.error('Final upload failed:', error);
            if (error.message.includes('401') || error.message.includes('Unauthorized')) {
                alert('Authentication failed. Please log in again.');
            } else {
                alert(`Failed to upload final copy: ${error.message}`);
            }
        } finally {
            setUploadLoading(false);
        }
    }

    // Refresh data function for status updates
    async function refreshData() {
        try {
            setLoading(true);
            const [uploadsData, taxReturnsData] = await Promise.all([
                apiGetUserUploads(selectedStatus, selectedYear),
                apiGetAllTaxReturnStatuses()
            ]);
            
            if (uploadsData && Array.isArray(uploadsData) && uploadsData.length > 0) {
                const processedData = uploadsData
                    .filter(upload => upload && upload.user && upload.year_display)
                    .map(upload => {
                        const userTaxReturns = taxReturnsData.filter(tr => 
                            tr.user === upload.user && tr.year === upload.year_display
                        );
                        
                        const draftCopy = userTaxReturns.find(tr => tr.type === 'draft');
                        const finalCopy = userTaxReturns.find(tr => tr.type === 'final');
                        
                        // Start with status from API response (backend has already determined it correctly)
                        let status = upload.status || 'pending';
                        
                        // Only override status if we have tax return data that indicates a more advanced state
                        if (finalCopy && finalCopy.user_received_at) {
                            status = 'completed';
                        } else if (finalCopy && finalCopy.status === 'final_uploaded') {
                            status = 'final_uploaded';
                        } else if (draftCopy && draftCopy.is_submitted) {
                            status = 'draft_approved';
                        } else if (draftCopy && draftCopy.status === 'draft_uploaded') {
                            status = 'draft_uploaded';
                        } else if (upload.status) {
                            // Use the status from API response (backend has already determined it correctly)
                            status = upload.status;
                        } else {
                            status = 'pending';
                        }
                        
                        return {
                            ...upload,
                            draft_copy: draftCopy ? draftCopy.filename : null,
                            draft_copy_id: draftCopy ? draftCopy.id : null,
                            final_copy: finalCopy ? finalCopy.filename : null,
                            final_copy_id: finalCopy ? finalCopy.id : null,
                            status: status
                        };
                    });
                
                setData(processedData);
                
                const statusLookup = {};
                if (taxReturnsData && taxReturnsData.length > 0) {
                    taxReturnsData.forEach(taxReturn => {
                        const key = `${taxReturn.user}_${taxReturn.year}`;
                        statusLookup[key] = taxReturn.status;
                    });
                }
                setTaxReturnStatus(statusLookup);
            }
        } catch (error) {
            console.error('Error refreshing data:', error);
        } finally {
            setLoading(false);
        }
    }

    // Download handlers for draft and final copies
    async function handleDownloadDraft(row) {
        try {
            await apiDownloadDraftCopy(row.draft_copy_id);
        } catch (error) {
            console.error('Draft download failed:', error);
            alert('Failed to download draft copy. Please try again.');
        }
    }

    async function handleDownloadFinal(row) {
        try {
            await apiDownloadFinalCopy(row.final_copy_id);
        } catch (error) {
            console.error('Final download failed:', error);
            alert('Failed to download final copy. Please try again.');
        }
    }

    // Group uploads by year
    const uploadsByYear = useMemo(() => {
        const byYear = {};
        data.forEach((d) => {
            byYear[d.year_display] = byYear[d.year_display] || [];
            byYear[d.year_display].push(d);
        });
        return byYear;
    }, [data]);

    const years = useMemo(() => {
        // If there's a search term, get years from filtered data
        if (search.trim()) {
            const searchFiltered = data.filter(d => {
                const s = search.trim().toLowerCase();
                return (d.user_name || '').toLowerCase().includes(s) ||
                       (d.original_filename || '').toLowerCase().includes(s) ||
                       (d.category_name || '').toLowerCase().includes(s) ||
                       String(d.year_display || '').includes(s);
            });
            const yearsFromSearch = [...new Set(searchFiltered.map(d => d.year_display))];
            return yearsFromSearch.sort((a, b) => b - a);
        }
        // Otherwise, get all years from original data
        return Object.keys(uploadsByYear).sort((a, b) => b - a);
    }, [uploadsByYear, search]);

    const filteredData = useMemo(() => {
        let filtered = data;

        // Filter by search first
        const s = search.trim().toLowerCase();
        if (s) {
            filtered = filtered.filter(
                d =>
                    (d.user_name || '').toLowerCase().includes(s) ||
                    (d.original_filename || '').toLowerCase().includes(s) ||
                    (d.category_name || '').toLowerCase().includes(s) ||
                    String(d.year_display || '').includes(s)
            );
        }

        // Then filter by year
        if (selectedYear !== "All Years") {
            filtered = filtered.filter(d => {
                // Handle both string and number comparisons
                return d.year_display === selectedYear || 
                       String(d.year_display) === String(selectedYear) ||
                       Number(d.year_display) === Number(selectedYear);
            });
        }
        
        // Status filter
        if (selectedStatus !== "All Statuses") {
            filtered = filtered.filter(d => d.status === selectedStatus);
        }

        return filtered;
    }, [data, search, selectedYear, selectedStatus]);

    // Get unique users for user selection
    const uniqueUsers = useMemo(() => {
        const users = {};
        filteredData.forEach(upload => {
            if (!users[upload.user]) {
                users[upload.user] = {
                    user_id: upload.user,
                    user_name: upload.user_name,
                    user_email: upload.user_email,
                    totalFiles: 0,
                    years: new Set()
                };
            }
            users[upload.user].totalFiles++;
            users[upload.user].years.add(upload.year_display);
        });

        return Object.values(users).map(user => ({
            ...user,
            years: Array.from(user.years).sort((a, b) => b - a)
        }));
    }, [filteredData]);

    // Filter users based on search
    const filteredUsers = useMemo(() => {
        if (!search.trim()) return uniqueUsers;
        const searchTerm = search.toLowerCase();
        return uniqueUsers.filter(user =>
            user.user_name.toLowerCase().includes(searchTerm) ||
            user.user_email.toLowerCase().includes(searchTerm)
        );
    }, [uniqueUsers, search]);

    // Get data for selected user based on year and name selection
    const selectedUserData = useMemo(() => {
        const currentUser = selectedUserForDetail || (selectedUserName ? uniqueUsers.find(u => u.user_name.toLowerCase().includes(selectedUserName.toLowerCase())) : null);
        
        if (!currentUser) return [];

        // Filter by year if specific year is selected
        let userUploads = filteredData.filter(upload => upload.user === currentUser.user_id);
        if (selectedYearForUser !== "All Years") {
            userUploads = userUploads.filter(upload => upload.year_display === selectedYearForUser);
        }

        return userUploads;
    }, [filteredData, selectedUserName, selectedUserForDetail, selectedYearForUser, uniqueUsers]);

    // Group selected user data by year
    const selectedUserGroupedData = useMemo(() => {
        const grouped = {};
        selectedUserData.forEach(upload => {
            const year = upload.year_display;
            const statusKey = `${upload.user}_${year}`;
            let status = taxReturnStatus[statusKey] || 'no_tax_return';
            
            // Use the status from the upload data itself, which already has the correct logic applied
            // The main data processing already handles the new upload after completion logic
            // Prioritize upload.status over taxReturnStatus
            if (upload.status) {
                status = upload.status;
            }
            
            if (!grouped[year]) {
                grouped[year] = {
                    year,
                    uploads: [],
                    totalFiles: 0,
                    status: status
                };
            }
            grouped[year].uploads.push(upload);
            grouped[year].totalFiles++;
        });
        const result = Object.values(grouped).sort((a, b) => b.year - a.year);
        // selectedUserGroupedData calculated successfully
        return result;
    }, [selectedUserData, taxReturnStatus]);

    // Get years for selected user - include all years but mark which ones are already filed
    const selectedUserYears = useMemo(() => {
        const currentUser = selectedUserForDetail || (selectedUserName ? uniqueUsers.find(u => u.user_name.toLowerCase().includes(selectedUserName.toLowerCase())) : null);
        if (!currentUser) return [];
        
        // Get all years from the user's uploads
        const userYears = currentUser.years || [];
        
        // Get all years from selectedUserGroupedData to include years that might be already filed
        const allYearsFromData = selectedUserGroupedData.map(g => g.year);
        
        // Combine and deduplicate
        const allYears = [...new Set([...userYears, ...allYearsFromData])].sort((a, b) => b - a);
        
        // Map years with their status to determine if already filed
        return allYears.map(year => {
            const yearData = selectedUserGroupedData.find(g => g.year === year);
            const isAlreadyFiled = yearData && (yearData.status === 'completed' || yearData.status === 'final_uploaded');
            return {
                year,
                isAlreadyFiled
            };
        });
    }, [selectedUserName, selectedUserForDetail, uniqueUsers, selectedUserGroupedData]);

    const customStyles = {
        rows: { style: { fontSize: 12, background: "#f8fafc", whiteSpace: "nowrap" } },
        table: { style: { width: "100%", minWidth: 800, background: "#fff", borderRadius: 20 } },
        headRow: { style: { fontWeight: 700, fontSize: 13, background: "#eceffd", color: "#4338ca" } },
        headCells: { style: { borderBottom: "1.5px solid #a5b4fc", padding: "6px 12px" } },
        cells: { style: { padding: "6px 12px" } },
        pagination: { style: { borderTop: "solid 1px #eceffd", color: "#4338ca", fontWeight: 600 } },
    };

    return (
        <section style={{
            userSelect: "none",
            width: "100%",
            //maxWidth: 1250,
            //margin: "0 auto",
            padding: 16,
        }}>
            {/* Success Message */}
            {successMessage && (
                <div style={{
                    background: "#dcfce7",
                    border: "1px solid #16a34a",
                    color: "#166534",
                    padding: "12px 16px",
                    borderRadius: "8px",
                    marginBottom: "16px",
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    fontSize: "14px",
                    fontWeight: "500",
                    position: "relative",
                    zIndex: 1000
                }}>
                    <AiOutlineCheckCircle style={{ fontSize: "18px" }} />
                    {successMessage}
                </div>
            )}
            
            {/* Authentication Error Message */}
            {!localStorage.getItem('adminAuthToken') && (
                <div style={{
                    background: "#fef2f2",
                    border: "1px solid #dc2626",
                    color: "#dc2626",
                    padding: "12px 16px",
                    borderRadius: "8px",
                    marginBottom: "16px",
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    fontSize: "14px",
                    fontWeight: "500"
                }}>
                    <span style={{ fontSize: "18px" }}>⚠️</span>
                    Authentication required. Please log in to access this page.
                </div>
            )}
            
            
            <div style={{
                display: "flex", alignItems: "center", justifyContent: "space-between",
                flexWrap: "wrap", gap: 10, marginBottom: 22
            }}>
                <div style={{ display: "flex", alignItems: "center", gap: 11 }}>
                    <AiOutlineCloudUpload style={{ fontSize: 32, color: "#217eed" }} />
                    <span style={{
                        display: "flex",
                        alignItems: "center", gap: 13,
                        marginBottom: 13,
                        fontSize: 38,
                        marginRight: 10,
                        color: "#215ea7",
                        verticalAlign: "middle",
                        filter: "drop-shadow(0px 1px 4px #b5beff77)",
                    }}>
                        User Uploads
                    </span>
                </div>
                {/*<div style={{ display: "flex", gap: "8px" }}>
                    <button
                        onClick={async () => {
                            // Clear all possible caches
                            localStorage.removeItem('adminDataCache');
                            localStorage.removeItem('adminAuthToken');
                            
                            // Clear browser cache
                            if ('caches' in window) {
                                const cacheNames = await caches.keys();
                                await Promise.all(
                                    cacheNames.map(cacheName => caches.delete(cacheName))
                                );
                            }
                            
                            // Force reload
                            window.location.reload();
                        }}
                        style={{
                            padding: "8px 16px",
                            background: "#4338ca",
                            color: "#fff",
                            border: "none",
                            borderRadius: "6px",
                            cursor: "pointer",
                            fontSize: "14px",
                            fontWeight: "500"
                        }}
                    >
                        Force Refresh
                    </button>
                    <button
                        onClick={refreshData}
                        style={{
                            padding: "8px 16px",
                            background: "#10b981",
                            color: "#fff",
                            border: "none",
                            borderRadius: "6px",
                            cursor: "pointer",
                            fontSize: "14px",
                            fontWeight: "500"
                        }}
                    >
                        Refresh Status
                    </button>
                </div>*/}
            </div>

            {/* Search and Year Selection */}
            <div style={{
                display: "flex",
                alignItems: "center",
                gap: "12px",
                marginBottom: "20px",
                padding: "12px 16px",
                background: "#f8fafc",
                border: "1px solid #e2e8f0",
                borderRadius: "8px",
                flexWrap: "wrap"
            }}>
                <AiOutlineSearch style={{ fontSize: "18px", color: "#4338ca" }} />
                    <input
                        type="text"
                        placeholder="Search user, file or category"
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                        style={{
                        padding: "8px 12px",
                        borderRadius: "6px",
                        border: "1px solid #d1d5db",
                        fontSize: "14px",
                        minWidth: "200px",
                        background: "#fff"
                    }}
                />
                
                <AiOutlineCalendar style={{ fontSize: "18px", color: "#4338ca" }} />
                <label style={{
                    fontSize: "14px",
                    fontWeight: "500",
                    color: "#374151"
                }}>
                    Year:
                </label>
                <select
                    value={selectedYear}
                    onChange={(e) => setSelectedYear(e.target.value)}
                            style={{
                        padding: "8px 12px",
                        border: "1px solid #d1d5db",
                        borderRadius: "6px",
                        fontSize: "14px",
                        minWidth: "120px",
                        background: "#fff"
                    }}
                >
                    <option value="All Years">All Years</option>
                    {years.map(year => (
                        <option key={year} value={year}>
                            {year}
                        </option>
                    ))}
                </select>
                
                <label style={{
                    fontSize: "14px",
                    fontWeight: "500",
                    color: "#374151"
                }}>
                    Status:
                </label>
                <select
                    value={selectedStatus}
                    onChange={(e) => setSelectedStatus(e.target.value)}
                    style={{
                        padding: "8px 12px",
                        border: "1px solid #d1d5db",
                        borderRadius: "6px",
                        fontSize: "14px",
                        minWidth: "150px",
                        background: "#fff"
                    }}
                >
                    <option value="All Statuses">All Statuses</option>
                    <option value="pending">Pending</option>
                    <option value="draft_uploaded">Draft Uploaded</option>
                    <option value="draft_approved">Draft Approved</option>
                    <option value="final_uploaded">Final Copy Sent</option>
                    <option value="completed">Completed</option>
                </select>
                
                {(search || selectedYear !== "All Years" || selectedStatus !== "All Statuses") && (
                    <button
                        onClick={resetFilters}
                        style={{
                            padding: "8px 12px",
                            border: "1px solid #d1d5db",
                            borderRadius: "6px",
                            background: "#fff",
                            color: "#374151",
                            fontSize: "14px",
                            cursor: "pointer",
                                display: "flex",
                                alignItems: "center",
                            gap: "4px"
                        }}
                    >
                        Reset
                    </button>
                )}
                
                <div style={{
                    fontSize: "12px",
                    color: "#6b7280",
                    marginLeft: "auto"
                }}>
                    {selectedYear === "All Years" ? `Showing all ${data.length} files` : `Showing ${filteredData.length} files for ${selectedYear}`}
                </div>
            </div>

            {/* Workflow Management Section */}
            {viewMode === 'overview' ? (
                <div style={{
                    background: "#f8fafc",
                    border: "1px solid #e2e8f0",
                    borderRadius: "12px",
                    padding: "20px",
                    marginBottom: "20px"
                }}>
                    <div style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        marginBottom: "16px"
                    }}>
                        <div style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "10px"
                        }}>
                            <AiOutlineFolderOpen style={{ fontSize: "20px", color: "#4338ca" }} />
                            <h3 style={{
                                margin: 0,
                                fontSize: "18px",
                                fontWeight: "600",
                                color: "#4338ca"
                            }}>
                                Tax Return Workflow Management
                            </h3>
                        </div>
                        <div style={{
                            fontSize: "14px",
                            color: "#6b7280"
                        }}>
                            {uniqueUsers.length} users with uploads
                        </div>
                    </div>

                    {/* Show empty state if no users */}
                    {uniqueUsers.length === 0 ? (
                        <div style={{
                            textAlign: "center",
                            padding: "40px 20px",
                            background: "#fff",
                            border: "1px solid #e2e8f0",
                            borderRadius: "8px",
                            marginBottom: "20px"
                        }}>
                            <AiOutlineFileText style={{ fontSize: "48px", color: "#9ca3af", marginBottom: "16px" }} />
                            <h4 style={{
                                margin: "0 0 8px 0",
                                fontSize: "18px",
                                fontWeight: "600",
                                color: "#374151"
                            }}>
                                No User Uploads Found
                            </h4>
                            <p style={{
                                margin: "0 0 16px 0",
                                fontSize: "14px",
                                color: "#6b7280"
                            }}>
                                No users have uploaded documents yet. Once users start uploading files, they will appear here.
                            </p>
                            <div style={{
                                fontSize: "12px",
                                color: "#9ca3af",
                                background: "#f9fafb",
                                padding: "8px 12px",
                                borderRadius: "6px",
                                display: "inline-block"
                            }}>
                                Database has been cleared - ready for new uploads
                            </div>
                        </div>
                    ) : (
                        <>
                            {/* User Selection Grid */}
                            <div style={{
                                display: "grid",
                                gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
                                gap: "12px",
                                marginBottom: "20px"
                            }}>
                                {filteredUsers.map((user, index) => (
                                    <div key={index} style={{
                                        background: "#fff",
                                        border: "1px solid #e2e8f0",
                                        borderRadius: "8px",
                                        padding: "16px",
                                        boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                                        cursor: "pointer",
                                        transition: "all 0.2s ease"
                                    }}
                                        onClick={() => switchToUserDetail(user)}
                                        onMouseEnter={(e) => {
                                            e.target.style.boxShadow = "0 4px 12px rgba(0,0,0,0.15)";
                                            e.target.style.transform = "translateY(-2px)";
                                        }}
                                        onMouseLeave={(e) => {
                                            e.target.style.boxShadow = "0 1px 3px rgba(0,0,0,0.1)";
                                            e.target.style.transform = "translateY(0)";
                                        }}
                                    >
                                        <div style={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: "8px",
                                            marginBottom: "8px"
                                        }}>
                                            <AiOutlineUser style={{ fontSize: "16px", color: "#4338ca" }} />
                                            <span style={{
                                                fontWeight: "600",
                                                fontSize: "15px",
                                                color: "#374151"
                                            }}>
                                                {user.user_name}
                                            </span>
                                        </div>

                                        <div style={{
                                            fontSize: "13px",
                                            color: "#6b7280",
                                            marginBottom: "8px"
                                        }}>
                                            {user.user_email}
                                        </div>

                                        <div style={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: "6px",
                                            marginBottom: "8px"
                                        }}>
                                            <AiOutlineFileText style={{ fontSize: "14px", color: "#6b7280" }} />
                                            <span style={{
                                                fontSize: "13px",
                                                color: "#6b7280"
                                            }}>
                                                {user.totalFiles} file(s)
                                            </span>
                                        </div>

                                        <div style={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: "6px"
                                        }}>
                                            <AiOutlineCalendar style={{ fontSize: "14px", color: "#6b7280" }} />
                                            <span style={{
                                                fontSize: "13px",
                                                color: "#6b7280"
                                            }}>
                                                Years: {user.years.join(", ")}
                                            </span>
                                        </div>

                                        <div style={{
                                            marginTop: "12px",
                                            padding: "6px 12px",
                                            background: "#4338ca",
                                            color: "#fff",
                                            borderRadius: "6px",
                                            fontSize: "12px",
                                            fontWeight: "600",
                                            textAlign: "center"
                                        }}>
                                            Click to Manage
                                        </div>
                                    </div>
                                ))}
                            </div>

                            {/* User Count Display */}
                            <div style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "12px",
                                marginTop: "16px"
                            }}>
                                <div style={{
                                    fontSize: "14px",
                                    color: "#6b7280"
                                }}>
                                    Showing {filteredUsers.length} of {uniqueUsers.length} users
                                </div>
                            </div>
                        </>
                    )}
                </div>
            ) : (
                /* User Detail View */
                <div style={{
                    background: "#f8fafc",
                    border: "1px solid #e2e8f0",
                    borderRadius: "12px",
                    padding: "20px",
                    marginBottom: "20px"
                }}>
                    <div style={{
                                display: "flex",
                                alignItems: "center",
                        justifyContent: "space-between",
                        marginBottom: "20px"
                    }}>
                        <div style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "12px"
                        }}>
                            <button
                                onClick={switchToOverview}
                                style={{
                                    background: "#6b7280",
                                    color: "#fff",
                                    border: "none",
                                    borderRadius: "6px",
                                    padding: "8px 12px",
                                    fontSize: "14px",
                                    cursor: "pointer",
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "6px"
                                }}
                            >
                                ← Back to Overview
                            </button>
                        </div>
                        <div style={{
                            fontSize: "14px",
                            color: "#6b7280"
                        }}>
                            {selectedUserData.length} total files
                        </div>
                    </div>

                    {/* User and Year Selection - Only show if no user is pre-selected */}
                    {!selectedUserForDetail && (
                        <div style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "16px",
                            marginBottom: "20px",
                            flexWrap: "wrap"
                        }}>
                            <div style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "8px"
                            }}>
                                <label style={{
                                    fontSize: "14px",
                                    fontWeight: "500",
                                    color: "#374151"
                                }}>
                                    Select User:
                                </label>
                                <select
                                    value={selectedUserName}
                                    onChange={handleUserNameSelection}
                                    style={{
                                        padding: "8px 12px",
                                        border: "1px solid #d1d5db",
                                        borderRadius: "6px",
                                        fontSize: "14px",
                                        minWidth: "200px"
                                    }}
                                >
                                    <option value="">Select a user...</option>
                                    {uniqueUsers.map(user => (
                                        <option key={user.user_id} value={user.user_name}>
                                            {user.user_name} ({user.user_email})
                                        </option>
                                    ))}
                                </select>
                            </div>

                            {selectedUserName && (
                                <div style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "8px"
                                }}>
                                    <label style={{
                                        fontSize: "14px",
                                        fontWeight: "500",
                                        color: "#374151"
                                    }}>
                                        Select Year:
                                    </label>
                                    <select
                                        value={selectedYearForUser}
                                        onChange={handleYearSelection}
                                    style={{
                                            padding: "8px 12px",
                                            border: "1px solid #d1d5db",
                                            borderRadius: "6px",
                                            fontSize: "14px",
                                            minWidth: "120px"
                                        }}
                                    >
                                        <option value="All Years">All Years</option>
                                        {selectedUserYears.map(({ year, isAlreadyFiled }) => (
                                            <option 
                                                key={year} 
                                                value={year}
                                                disabled={isAlreadyFiled}
                                                style={{
                                                    color: isAlreadyFiled ? '#9ca3af' : '#000',
                                                    fontStyle: isAlreadyFiled ? 'italic' : 'normal'
                                                }}
                                            >
                                                {year} {isAlreadyFiled ? '(Already Filed)' : ''}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            )}
                        </div>
                    )}

                    {/* User Info Display */}
                    {(selectedUserName || selectedUserForDetail) && (
                        <div style={{
                            background: "#fff",
                            border: "1px solid #e2e8f0",
                            borderRadius: "8px",
                            padding: "16px",
                            marginBottom: "20px"
                        }}>
                            <div style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "12px"
                            }}>
                                <div>
                                    <h3 style={{
                                        margin: 0,
                                        fontSize: "18px",
                                        fontWeight: "600",
                                        color: "#374151"
                                    }}>
                                        {selectedUserForDetail ? selectedUserForDetail.user_name : selectedUserName}
                                    </h3>
                                    <p style={{
                                        margin: 0,
                                        fontSize: "14px",
                                        color: "#6b7280"
                                    }}>
                                        {selectedUserForDetail ? selectedUserForDetail.user_email : uniqueUsers.find(u => u.user_name === selectedUserName)?.user_email}
                                    </p>
                                </div>
                            </div>
                        </div>
                    )}



                    {/* User's Workflow Card with Year Navigation */}
                    {(selectedUserName || selectedUserForDetail) && selectedUserGroupedData.length > 0 ? (
                        <div style={{
                            background: "#fff",
                            border: "1px solid #e2e8f0",
                            borderRadius: "8px",
                            padding: "20px",
                            boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                            position: "relative"
                        }}>
                            {/* Year Navigation Header */}
                            <div style={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                marginBottom: "20px"
                            }}>
                                <div style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "12px"
                                }}>
                                    <button
                                        onClick={goToPreviousYear}
                                        disabled={currentYearIndex === 0}
                                        style={{
                                            background: currentYearIndex === 0 ? "#f3f4f6" : "#3b82f6",
                                            color: currentYearIndex === 0 ? "#9ca3af" : "#fff",
                                            border: "none",
                                            borderRadius: "50%",
                                            width: "40px",
                                            height: "40px",
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            cursor: currentYearIndex === 0 ? "not-allowed" : "pointer",
                                            fontSize: "16px"
                                        }}
                                    >
                                        <AiOutlineLeft />
                                    </button>
                                    
                                    <div style={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: "6px"
                                    }}>
                                        <AiOutlineCalendar style={{ fontSize: "18px", color: "#6b7280" }} />
                                        <span style={{
                                            fontWeight: "600",
                                            fontSize: "18px",
                                            color: "#374151"
                                        }}>
                                            Year: {selectedUserGroupedData[currentYearIndex]?.year}
                                        </span>
                                    </div>
                                    
                                    <button
                                        onClick={goToNextYear}
                                        disabled={currentYearIndex === selectedUserGroupedData.length - 1}
                                        style={{
                                            background: currentYearIndex === selectedUserGroupedData.length - 1 ? "#f3f4f6" : "#3b82f6",
                                            color: currentYearIndex === selectedUserGroupedData.length - 1 ? "#9ca3af" : "#fff",
                                            border: "none",
                                            borderRadius: "50%",
                                            width: "40px",
                                            height: "40px",
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            cursor: currentYearIndex === selectedUserGroupedData.length - 1 ? "not-allowed" : "pointer",
                                            fontSize: "16px"
                                        }}
                                    >
                                        <AiOutlineRight />
                                    </button>
                                </div>

                                <div style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "8px"
                                }}>
                                    <span style={{
                                        fontSize: "14px",
                                        color: "#6b7280"
                                    }}>
                                        {currentYearIndex + 1} of {selectedUserGroupedData.length}
                                    </span>
                                    <div style={{
                                        background: selectedUserGroupedData[currentYearIndex]?.status === 'completed' ? "#dcfce7" :
                                            selectedUserGroupedData[currentYearIndex]?.status === 'final_uploaded' ? "#fef3c7" :
                                            selectedUserGroupedData[currentYearIndex]?.status === 'draft_approved' ? "#dbeafe" :
                                            selectedUserGroupedData[currentYearIndex]?.status === 'draft_uploaded' ? "#e0e7ff" :
                                            selectedUserGroupedData[currentYearIndex]?.status === 'pending' ? "#fef3c7" : 
                                            selectedUserGroupedData[currentYearIndex]?.status === 'no_tax_return' ? "#f3f4f6" : "#f3f4f6",
                                        color: selectedUserGroupedData[currentYearIndex]?.status === 'completed' ? "#166534" :
                                            selectedUserGroupedData[currentYearIndex]?.status === 'final_uploaded' ? "#92400e" :
                                            selectedUserGroupedData[currentYearIndex]?.status === 'draft_approved' ? "#1e40af" :
                                            selectedUserGroupedData[currentYearIndex]?.status === 'draft_uploaded' ? "#3730a3" :
                                            selectedUserGroupedData[currentYearIndex]?.status === 'pending' ? "#92400e" : 
                                            selectedUserGroupedData[currentYearIndex]?.status === 'no_tax_return' ? "#6b7280" : "#6b7280",
                                        padding: "6px 12px",
                                        borderRadius: "6px",
                                        fontSize: "12px",
                                        fontWeight: "600"
                                    }}>
                                        {selectedUserGroupedData[currentYearIndex]?.status === 'no_tax_return' ? 'No Tax Return' : 
                                         selectedUserGroupedData[currentYearIndex]?.status === 'completed' ? 'Completed' :
                                         selectedUserGroupedData[currentYearIndex]?.status === 'final_uploaded' ? 'Final Copy Sent' :
                                         selectedUserGroupedData[currentYearIndex]?.status === 'draft_approved' ? 'Draft Approved' :
                                         selectedUserGroupedData[currentYearIndex]?.status === 'draft_uploaded' ? 'Draft Uploaded' :
                                         selectedUserGroupedData[currentYearIndex]?.status === 'pending' ? 'Pending' :
                                         selectedUserGroupedData[currentYearIndex]?.status}
                                    </div>
                                </div>
                            </div>

                            {/* Current Year Content */}
                            {selectedUserGroupedData[currentYearIndex] && (
                                <div>
                                    <div style={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: "6px",
                                        marginBottom: "20px"
                                    }}>
                                        <AiOutlineFileText style={{ fontSize: "16px", color: "#6b7280" }} />
                                        <span style={{
                                            fontSize: "14px",
                                            color: "#6b7280"
                                        }}>
                                            {selectedUserGroupedData[currentYearIndex].totalFiles} file(s) uploaded
                                        </span>
                                    </div>

                                    <div style={{
                                        display: "flex",
                                        gap: "12px",
                                        flexWrap: "wrap"
                                    }}>
                                        <button
                                            onClick={() => {
                                                const currentUser = selectedUserForDetail || uniqueUsers.find(u => u.user_name.toLowerCase().includes(selectedUserName.toLowerCase()));
                                                openDraftModal({
                                                    user_id: currentUser.user_id,
                                                    user_name: currentUser.user_name,
                                                    year_display: selectedUserGroupedData[currentYearIndex].year
                                                });
                                            }}
                                            disabled={selectedUserGroupedData[currentYearIndex].status === 'draft_approved' || selectedUserGroupedData[currentYearIndex].status === 'final_uploaded' || selectedUserGroupedData[currentYearIndex].status === 'completed'}
                                            style={{
                                                background: (selectedUserGroupedData[currentYearIndex].status === 'draft_approved' || selectedUserGroupedData[currentYearIndex].status === 'final_uploaded' || selectedUserGroupedData[currentYearIndex].status === 'completed') ? "#9ca3af" : "#3b82f6",
                                                color: "#fff",
                                                border: "none",
                                                borderRadius: "6px",
                                                padding: "10px 16px",
                                                fontSize: "14px",
                                                cursor: (selectedUserGroupedData[currentYearIndex].status === 'draft_approved' || selectedUserGroupedData[currentYearIndex].status === 'final_uploaded' || selectedUserGroupedData[currentYearIndex].status === 'completed') ? "not-allowed" : "pointer",
                                                display: "flex",
                                                alignItems: "center",
                                                gap: "6px",
                                                fontWeight: "500"
                                            }}
                                            title={selectedUserGroupedData[currentYearIndex].status === 'draft_approved' ? "Draft already approved. Upload Final instead." : 
                                                   selectedUserGroupedData[currentYearIndex].status === 'final_uploaded' ? "Final copy already sent. Waiting for user acknowledgment." :
                                                   selectedUserGroupedData[currentYearIndex].status === 'completed' ? "Workflow completed." :
                                                   "Upload Draft Copy"}
                                        >
                                            <AiOutlineFileText style={{ fontSize: "14px" }} />
                                            Upload Draft
                                        </button>
                                        <button
                                            onClick={() => {
                                                const currentUser = selectedUserForDetail || uniqueUsers.find(u => u.user_name.toLowerCase().includes(selectedUserName.toLowerCase()));
                                                openFinalModal({
                                                    user_id: currentUser.user_id,
                                                    user_name: currentUser.user_name,
                                                    year_display: selectedUserGroupedData[currentYearIndex].year
                                                });
                                            }}
                                            disabled={selectedUserGroupedData[currentYearIndex].status === 'user_received' || selectedUserGroupedData[currentYearIndex].status === 'completed' || (selectedUserGroupedData[currentYearIndex].status !== 'draft_approved' && selectedUserGroupedData[currentYearIndex].status !== 'final_uploaded')}
                                            style={{
                                                background: selectedUserGroupedData[currentYearIndex].status === 'user_received' ? "#6b7280" :
                                                           selectedUserGroupedData[currentYearIndex].status === 'completed' ? "#6b7280" :
                                                           (selectedUserGroupedData[currentYearIndex].status === 'draft_approved' || selectedUserGroupedData[currentYearIndex].status === 'final_uploaded') ? "#10b981" : "#9ca3af",
                                                color: "#fff",
                                                border: "none",
                                                borderRadius: "6px",
                                                padding: "10px 16px",
                                                fontSize: "14px",
                                                cursor: selectedUserGroupedData[currentYearIndex].status === 'user_received' ? "not-allowed" :
                                                       selectedUserGroupedData[currentYearIndex].status === 'completed' ? "not-allowed" :
                                                       (selectedUserGroupedData[currentYearIndex].status === 'draft_approved' || selectedUserGroupedData[currentYearIndex].status === 'final_uploaded') ? "pointer" : "not-allowed",
                                                display: "flex",
                                                alignItems: "center",
                                                gap: "6px",
                                                fontWeight: "500"
                                            }}
                                            title={selectedUserGroupedData[currentYearIndex].status === 'user_received' ? "User has received the final copy" :
                                                   selectedUserGroupedData[currentYearIndex].status === 'completed' ? "Final copy already sent" :
                                                   selectedUserGroupedData[currentYearIndex].status === 'draft_approved' ? "Upload Final Copy (Draft Approved)" : 
                                                   selectedUserGroupedData[currentYearIndex].status === 'final_uploaded' ? "Final copy already uploaded" : 
                                                   "Draft must be approved first"}
                                        >
                                            <AiOutlineCheckCircle style={{ fontSize: "14px" }} />
                                            Upload Final
                                        </button>
                                        <button
                                            onClick={async () => {
                                                try {
                                                    const currentUser = selectedUserForDetail || uniqueUsers.find(u => u.user_name.toLowerCase().includes(selectedUserName.toLowerCase()));
                                                    const year = selectedUserGroupedData[currentYearIndex].year;
                                                    
                                                    console.log('Current user:', currentUser);
                                                    console.log('Year:', year);
                                                    
                                                    if (!currentUser) {
                                                        alert('User not found');
                                                        return;
                                                    }
                                                    
                                                    if (!currentUser.user_id) {
                                                        alert('User ID not found');
                                                        return;
                                                    }
                                                    
                                                    setLoading(true);
                                                    await apiDownloadUserFilesZip(currentUser.user_id, year);
                                                } catch (error) {
                                                    console.error('Download failed:', error);
                                                    alert('Download failed: ' + error.message);
                                                } finally {
                                                    setLoading(false);
                                                }
                                            }}
                                            style={{
                                                background: "#059669",
                                                color: "#fff",
                                                border: "none",
                                                borderRadius: "6px",
                                                padding: "10px 16px",
                                                fontSize: "14px",
                                                cursor: "pointer",
                                                display: "flex",
                                                alignItems: "center",
                                                gap: "6px",
                                                fontWeight: "500"
                                            }}
                                        >
                                            <AiOutlineDownload style={{ fontSize: "14px" }} />
                                            Download All Files
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    ) : (selectedUserName || selectedUserForDetail) && selectedUserGroupedData.length === 0 ? (
                        <div style={{
                            background: "#fff",
                            border: "1px solid #e2e8f0",
                            borderRadius: "8px",
                            padding: "40px 20px",
                            textAlign: "center",
                            marginBottom: "20px"
                        }}>
                            <AiOutlineFileText style={{ fontSize: "48px", color: "#9ca3af", marginBottom: "16px" }} />
                            <h4 style={{
                                margin: "0 0 8px 0",
                                fontSize: "18px",
                                fontWeight: "600",
                                color: "#374151"
                            }}>
                                No Files for This User
                            </h4>
                            <p style={{
                                margin: "0 0 16px 0",
                                fontSize: "14px",
                                color: "#6b7280"
                            }}>
                                {selectedUserForDetail ? selectedUserForDetail.user_name : selectedUserName} has not uploaded any documents yet.
                            </p>
                            <div style={{
                                fontSize: "12px",
                                color: "#9ca3af",
                                background: "#f9fafb",
                                padding: "8px 12px",
                                borderRadius: "6px",
                                display: "inline-block"
                            }}>
                                Database cleared - ready for new uploads
                            </div>
                        </div>
                    ) : null}
                </div>
            )}
            
            <DataTable
                columns={columns}
                data={viewMode === 'user-detail' ? selectedUserData : filteredData}
                highlightOnHover
                striped
                pagination
                paginationPerPage={perPage}
                paginationRowsPerPageOptions={[10, 20, 30, 40, 50]}
                onChangeRowsPerPage={handlePerRowsChange}
                onChangePage={handlePageChange}
                paginationDefaultPage={currentPage}
                progressPending={loading}
                noHeader
                responsive
                persistTableHead
                customStyles={customStyles}
                noDataComponent={
                    <div style={{
                        textAlign: "center",
                        padding: "40px 20px",
                        color: "#6b7280"
                    }}>
                        <AiOutlineFileText style={{ fontSize: "48px", color: "#9ca3af", marginBottom: "16px" }} />
                        <h4 style={{
                            margin: "0 0 8px 0",
                            fontSize: "18px",
                            fontWeight: "600",
                            color: "#374151"
                        }}>
                            No Data Available
                        </h4>
                        <p style={{
                            margin: "0 0 16px 0",
                            fontSize: "14px",
                            color: "#6b7280"
                        }}>
                            {data.length === 0 ? 
                                "No user uploads found. The database has been cleared and is ready for new uploads." :
                                "No data matches your current search or filter criteria."
                            }
                        </p>
                        {data.length === 0 && (
                            <div style={{
                                fontSize: "12px",
                                color: "#9ca3af",
                                background: "#f9fafb",
                                padding: "8px 12px",
                                borderRadius: "6px",
                                display: "inline-block"
                            }}>
                                Database cleared - ready for new uploads
                            </div>
                        )}
                    </div>
                }
            />

            {/* Draft Upload Modal */}
            {showDraftModal && selectedUser && (
                <div style={{
                    position: "fixed",
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: "rgba(0,0,0,0.5)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    zIndex: 1000
                }}>
                    <div style={{
                        background: "#fff",
                        borderRadius: "12px",
                        padding: "24px",
                        maxWidth: "500px",
                        width: "90%",
                        boxShadow: "0 10px 25px rgba(0,0,0,0.2)"
                    }}>
                        <h3 style={{
                            margin: "0 0 16px 0",
                            fontSize: "18px",
                            fontWeight: "600",
                            color: "#374151"
                        }}>
                            Upload Draft Copy
                        </h3>

                        <div style={{ marginBottom: "16px" }}>
                            <p style={{ margin: "0 0 8px 0", fontSize: "14px", color: "#6b7280" }}>
                                <strong>User:</strong> {selectedUser.user_name}
                            </p>
                            <p style={{ margin: "0 0 16px 0", fontSize: "14px", color: "#6b7280" }}>
                                <strong>Year:</strong> {selectedUser.year_display}
                            </p>
                        </div>

                        <div style={{ marginBottom: "20px" }}>
                            <label style={{
                                display: "block",
                                marginBottom: "8px",
                                fontSize: "14px",
                                fontWeight: "500",
                                color: "#374151"
                            }}>
                                Select Draft File
                            </label>
                            <input
                                type="file"
                                accept=".pdf,.doc,.docx,.txt"
                                onChange={handleDraftFileChange}
                                style={{
                                    width: "100%",
                                    padding: "8px",
                                    border: "1px solid #d1d5db",
                                    borderRadius: "6px",
                                    fontSize: "14px"
                                }}
                            />
                            {draftFile && (
                                <p style={{
                                    margin: "8px 0 0 0",
                                    fontSize: "13px",
                                    color: "#059669"
                                }}>
                                    Selected: {draftFile.name}
                                </p>
                            )}
                        </div>

                        <div style={{
                            display: "flex",
                            gap: "12px",
                            justifyContent: "flex-end"
                        }}>
                            <button
                                onClick={closeDraftModal}
                                style={{
                                    padding: "8px 16px",
                                    border: "1px solid #d1d5db",
                                    borderRadius: "6px",
                                    background: "#fff",
                                    color: "#374151",
                                    fontSize: "14px",
                                    cursor: "pointer"
                                }}
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleDraftUpload}
                                disabled={!draftFile || uploadLoading}
                                style={{
                                    padding: "8px 16px",
                                    border: "none",
                                    borderRadius: "6px",
                                    background: !draftFile || uploadLoading ? "#9ca3af" : "#3b82f6",
                                    color: "#fff",
                                    fontSize: "14px",
                                    cursor: !draftFile || uploadLoading ? "not-allowed" : "pointer"
                                }}
                            >
                                {uploadLoading ? "Uploading..." : "Upload Draft"}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Final Upload Modal */}
            {showFinalModal && selectedUser && (
                <div style={{
                    position: "fixed",
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: "rgba(0,0,0,0.5)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    zIndex: 1000
                }}>
                    <div style={{
                        background: "#fff",
                        borderRadius: "12px",
                        padding: "24px",
                        maxWidth: "500px",
                        width: "90%",
                        boxShadow: "0 10px 25px rgba(0,0,0,0.2)"
                    }}>
                        <h3 style={{
                            margin: "0 0 16px 0",
                            fontSize: "18px",
                            fontWeight: "600",
                            color: "#374151"
                        }}>
                            Upload Final Copy
                        </h3>

                        <div style={{ marginBottom: "16px" }}>
                            <p style={{ margin: "0 0 8px 0", fontSize: "14px", color: "#6b7280" }}>
                                <strong>User:</strong> {selectedUser.user_name}
                            </p>
                            <p style={{ margin: "0 0 16px 0", fontSize: "14px", color: "#6b7280" }}>
                                <strong>Year:</strong> {selectedUser.year_display}
                            </p>
                        </div>

                        <div style={{ marginBottom: "20px" }}>
                            <label style={{
                                display: "block",
                                marginBottom: "8px",
                                fontSize: "14px",
                                fontWeight: "500",
                                color: "#374151"
                            }}>
                                Select Final File
                            </label>
                            <input
                                type="file"
                                accept=".pdf,.doc,.docx,.txt"
                                onChange={handleFinalFileChange}
                                style={{
                                    width: "100%",
                                    padding: "8px",
                                    border: "1px solid #d1d5db",
                                    borderRadius: "6px",
                                    fontSize: "14px"
                                }}
                            />
                            {finalFile && (
                                <p style={{
                                    margin: "8px 0 0 0",
                                    fontSize: "13px",
                                    color: "#059669"
                                }}>
                                    Selected: {finalFile.name}
                                </p>
                            )}
                        </div>

                        <div style={{
                            display: "flex",
                            gap: "12px",
                            justifyContent: "flex-end"
                        }}>
                            <button
                                onClick={closeFinalModal}
                                style={{
                                    padding: "8px 16px",
                                    border: "1px solid #d1d5db",
                                    borderRadius: "6px",
                                    background: "#fff",
                                    color: "#374151",
                                    fontSize: "14px",
                                    cursor: "pointer"
                                }}
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleFinalUpload}
                                disabled={!finalFile || uploadLoading}
                                style={{
                                    padding: "8px 16px",
                                    border: "none",
                                    borderRadius: "6px",
                                    background: !finalFile || uploadLoading ? "#9ca3af" : "#10b981",
                                    color: "#fff",
                                    fontSize: "14px",
                                    cursor: !finalFile || uploadLoading ? "not-allowed" : "pointer"
                                }}
                            >
                                {uploadLoading ? "Uploading..." : "Upload Final"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </section>
    );
}
